import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * First-visit tutorial modal that walks attendees through the guided
 * slot-by-slot registration flow on the EventBrochure page.
 *
 * Renders a 4-step walkthrough with Next/Back/Skip navigation, step
 * indicator dots, and accessible markup following the same overlay
 * pattern as EarlyBirdPasswordModal.
 *
 * Props:
 *   onDismiss  - callback() invoked when the user dismisses the modal
 *   brandColor - optional hex color for primary buttons (default #0284c7)
 */

const STEPS = [
  {
    title: 'Browse Sessions by Time Slot',
    text: 'Sessions are organized by time slot. Each slot shows the available sessions happening at that time.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
  },
  {
    title: 'Select One Session Per Slot',
    text: "Click \"Select\" to choose a session. You'll automatically advance to the next time slot. Use the progress bar at the top to jump between slots.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  {
    title: 'Finalize Your Registration',
    text: "Once you've selected a session for every time slot, click \"Finalize Registration\" at the bottom to complete your sign-up.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
      </svg>
    ),
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  {
    title: 'Helpful Tips',
    text: 'Use the filter button to narrow sessions by Licensure or Audience. Search by title or presenter name. You can also download a PDF of all sessions to review offline.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
  },
];

export default function EventTutorialModal({ onDismiss, brandColor = '#0284c7' }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
  const [animating, setAnimating] = useState(false);
  const primaryBtnRef = useRef(null);

  const isLastStep = currentStep === STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  // Auto-focus the primary button on mount and step change
  useEffect(() => {
    primaryBtnRef.current?.focus();
  }, [currentStep]);

  // Dismiss on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onDismiss();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onDismiss]);

  const goTo = (nextIndex) => {
    if (animating) return;
    setDirection(nextIndex > currentStep ? 1 : -1);
    setAnimating(true);
    // Brief transition delay for smooth step change
    setTimeout(() => {
      setCurrentStep(nextIndex);
      setAnimating(false);
    }, 150);
  };

  const handleNext = () => {
    if (isLastStep) {
      onDismiss();
    } else {
      goTo(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      goTo(currentStep - 1);
    }
  };

  const step = STEPS[currentStep];

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center px-4 pt-24 pb-4"
      role="dialog"
      aria-modal="true"
      aria-label="Event registration tutorial"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onDismiss}
      />

      {/* Modal card */}
      <div
        className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Step content with transition */}
        <div
          className="transition-opacity duration-150 ease-in-out"
          style={{ opacity: animating ? 0 : 1 }}
        >
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className={`w-14 h-14 rounded-full ${step.iconBg} flex items-center justify-center ${step.iconColor}`}>
              {step.icon}
            </div>
          </div>

          {/* Step counter */}
          <p className="text-xs font-medium text-stone-400 text-center mb-1 uppercase tracking-wider">
            Step {currentStep + 1} of {STEPS.length}
          </p>

          {/* Title */}
          <h3 className="text-lg font-semibold text-stone-900 text-center mb-2">
            {step.title}
          </h3>

          {/* Description */}
          <p className="text-sm text-stone-600 text-center leading-relaxed mb-6">
            {step.text}
          </p>
        </div>

        {/* Step indicator dots */}
        <div className="flex justify-center gap-2 mb-5" aria-hidden="true">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors duration-150 ${i !== currentStep ? 'bg-stone-200' : ''}`}
              style={i === currentStep ? { backgroundColor: brandColor } : undefined}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          {/* Left: Skip link */}
          <button
            type="button"
            onClick={onDismiss}
            className="text-sm text-stone-400 hover:text-stone-600 transition-colors"
          >
            Skip
          </button>

          {/* Right: Back + Next/Got it */}
          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <button
                type="button"
                onClick={handleBack}
                disabled={animating}
                className="px-4 py-2 text-sm font-medium text-stone-600 hover:text-stone-800 hover:bg-stone-100 rounded-lg transition-colors"
              >
                Back
              </button>
            )}
            <button
              ref={primaryBtnRef}
              type="button"
              onClick={handleNext}
              disabled={animating}
              className="px-5 py-2 text-sm font-semibold text-white rounded-lg transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: brandColor }}
            >
              {isLastStep ? 'Got it!' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
